import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Inventory as ReceiveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  PostAdd as AddToStockIcon,
  Undo as RevertStockIcon,
  SwapHoriz as StatusIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

interface Product {
  id: number;
  sku: string;
  name: string;
  category_name: string;
  unit_size: string;
  qty_in_box: number;
  image_url: string;
}

interface ShipmentItem {
  id: number;
  product_id: number;
  product: Product;
  expected_box_quantity: number;
  expected_single_quantity: number;
  received_box_quantity: number;
  received_single_quantity: number;
  is_new_product: boolean;
  new_product_data?: any;
  notes?: string;
  sku: string;
  product_name: string;
  category_name: string;
  unit_size: string;
  qty_in_box: number;
  image_url: string;
}

interface ShipmentData {
  id: number;
  shipment_number: string;
  from_location: string;
  expected_date: string;
  received_date?: string;
  status: 'pending' | 'in_transit' | 'received' | 'cancelled';
  notes: string;
  created_by_name: string;
  created_by_lastname: string;
  received_by_name?: string;
  received_by_lastname?: string;
  stock_added: boolean;
  stock_added_date?: string;
  stock_added_by_name?: string;
  stock_added_by_lastname?: string;
  stock_reverted: boolean;
  stock_reverted_date?: string;
  stock_reverted_by_name?: string;
  stock_reverted_by_lastname?: string;
  created_at: string;
  items: ShipmentItem[];
}

const ShipmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, hasPermission, hasRole } = useAuth();
  const [shipment, setShipment] = useState<ShipmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [receivedItems, setReceivedItems] = useState<{ [key: number]: { box: number; single: number; notes: string } }>({});
  const [selectAll, setSelectAll] = useState(false);
  const [addingToStock, setAddingToStock] = useState(false);
  const [revertingStock, setRevertingStock] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchShipment();
  }, [id]);

  const fetchShipment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shipments/${id}`);
      if (response.data.success) {
        const shipmentData = response.data.data;
        setShipment(shipmentData);
        
        // Initialize received items state
        const initialReceivedItems: { [key: number]: { box: number; single: number; notes: string } } = {};
        shipmentData.items.forEach((item: ShipmentItem) => {
          initialReceivedItems[item.id] = {
            box: item.received_box_quantity || item.expected_box_quantity,
            single: item.received_single_quantity || item.expected_single_quantity,
            notes: item.notes || ''
          };
        });
        setReceivedItems(initialReceivedItems);
      }
    } catch (error: any) {
      setError('Failed to fetch shipment details');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveQuantityChange = (itemId: number, field: 'box' | 'single', value: number) => {
    setReceivedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: Math.max(0, value)
      }
    }));
  };

  const handleReceiveNotesChange = (itemId: number, notes: string) => {
    setReceivedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        notes
      }
    }));
  };

  const handleSelectAll = () => {
    if (!shipment) return;
    
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    const updatedReceivedItems = { ...receivedItems };
    shipment.items.forEach(item => {
      if (newSelectAll) {
        updatedReceivedItems[item.id] = {
          box: item.expected_box_quantity,
          single: item.expected_single_quantity,
          notes: updatedReceivedItems[item.id]?.notes || ''
        };
      } else {
        updatedReceivedItems[item.id] = {
          box: 0,
          single: 0,
          notes: updatedReceivedItems[item.id]?.notes || ''
        };
      }
    });
    setReceivedItems(updatedReceivedItems);
  };

  const handleReceiveShipment = async () => {
    if (!shipment) return;

    try {
      setReceiving(true);
      setError(null);

      // Prepare receive data
      const receiveData = {
        received_items: shipment.items.map(item => ({
          item_id: item.id,
          received_box_quantity: receivedItems[item.id]?.box || 0,
          received_single_quantity: receivedItems[item.id]?.single || 0,
          notes: receivedItems[item.id]?.notes || ''
        }))
      };

      const response = await api.post(`/shipments/${id}/receive`, receiveData);
      if (response.data.success) {
        await fetchShipment(); // Refresh data
        setReceiveDialogOpen(false);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to receive shipment');
    } finally {
      setReceiving(false);
    }
  };

  const handleAddToStock = async () => {
    if (!shipment) return;

    try {
      setAddingToStock(true);
      setError(null);

      const response = await api.post(`/shipments/${id}/add-to-stock`);
      if (response.data.success) {
        await fetchShipment(); // Refresh data
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to add stock');
    } finally {
      setAddingToStock(false);
    }
  };

  const handleRevertStock = async () => {
    if (!shipment) return;

    try {
      setRevertingStock(true);
      setError(null);

      const response = await api.post(`/shipments/${id}/revert-stock`);
      if (response.data.success) {
        await fetchShipment(); // Refresh data
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to revert stock');
    } finally {
      setRevertingStock(false);
    }
  };

  const handleStatusChange = async () => {
    if (!shipment || !newStatus) return;

    try {
      setUpdatingStatus(true);
      setError(null);

      const response = await api.patch(`/shipments/${id}/status`, { status: newStatus });
      if (response.data.success) {
        await fetchShipment(); // Refresh data
        setStatusDialogOpen(false);
        setNewStatus('');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openStatusDialog = () => {
    setNewStatus(shipment?.status || '');
    setStatusDialogOpen(true);
  };

  const getAvailableStatuses = () => {
    if (!shipment) return [];
    
    const allStatuses = [
      { value: 'pending', label: 'Pending' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'received', label: 'Received' },
      { value: 'cancelled', label: 'Cancelled' }
    ];
    
    // Filter out current status
    return allStatuses.filter(status => status.value !== shipment.status);
  };

  const handleDownloadPDF = async () => {
    if (!shipment) return;

    try {
      setError(null);
      console.log('Starting PDF download for shipment:', id);
      
      // Use the same API instance to ensure consistent base URL and headers
      const response = await api.get(`/shipments/${id}/pdf`, {
        responseType: 'blob', // Important: specify blob response type
        timeout: 30000, // 30 second timeout
      });

      console.log('PDF response received, size:', response.data.size);

      // Check if response is actually a PDF
      if (response.data.type !== 'application/pdf') {
        console.error('Response is not a PDF:', response.data.type);
        throw new Error('Invalid PDF response');
      }

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipment-${shipment.shipment_number}.pdf`;
      link.target = '_blank'; // Open in new tab if download fails
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a brief delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('PDF download initiated successfully');
      
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      
      // More specific error messages
      let errorMessage = 'Failed to download PDF. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'Shipment not found.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download this PDF.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while generating PDF. Please try again later.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        errorMessage = 'Network error or timeout. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_transit':
        return 'info';
      case 'received':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTotalExpectedQuantity = () => {
    if (!shipment) return 0;
    return shipment.items.reduce((total, item) => 
      total + (item.expected_box_quantity * item.qty_in_box) + item.expected_single_quantity, 0
    );
  };

  const getTotalReceivedQuantity = () => {
    if (!shipment) return 0;
    return shipment.items.reduce((total, item) => 
      total + (item.received_box_quantity * item.qty_in_box) + item.received_single_quantity, 0
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!shipment) {
    return (
      <Box>
        <Alert severity="error">Shipment not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate('/shipments')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Shipment Details
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {/* Download PDF Button - Always available */}
            {(hasPermission('shipments.read') || hasRole(['super_admin', 'admin', 'shipment_manager'])) && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
              >
                Download PDF
              </Button>
            )}
            
            {(hasPermission('shipments.update') || hasRole(['super_admin', 'admin'])) && (
              <Button
                variant="outlined"
                startIcon={<StatusIcon />}
                onClick={openStatusDialog}
              >
                Change Status
              </Button>
            )}
            {/* Edit Button - Show for non-received shipments */}
            {(hasPermission('shipments.update') || hasRole(['super_admin', 'admin'])) && 
             !['received', 'cancelled'].includes(shipment.status) && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/shipments/${id}/edit`)}
              >
                Edit
              </Button>
            )}
            
            {/* Receive Button - Show for pending/in_transit shipments */}
            {(hasPermission('shipments.receive') || hasRole(['super_admin', 'admin'])) && 
             ['pending', 'in_transit'].includes(shipment.status) && (
              <Button
                variant="contained"
                startIcon={<ReceiveIcon />}
                onClick={() => setReceiveDialogOpen(true)}
              >
                Receive Shipment
              </Button>
            )}
            
            {/* Add to Stock Button - Show for received shipments without stock added */}
            {(hasPermission('shipments.manage_stock') || hasRole(['super_admin', 'admin'])) && 
             shipment.status === 'received' && !shipment.stock_added ? (
              <Button
                variant="contained"
                color="success"
                startIcon={addingToStock ? <CircularProgress size={16} /> : <AddToStockIcon />}
                onClick={handleAddToStock}
                disabled={addingToStock}
              >
                {addingToStock ? 'Adding...' : 'Add to Stock'}
              </Button>
            ) : null}
            
            {/* Revert Stock Button - Show for received shipments with stock added */}
            {(hasPermission('shipments.manage_stock') || hasRole(['super_admin', 'admin'])) && 
             shipment.status === 'received' && shipment.stock_added && !shipment.stock_reverted ? (
              <Button
                variant="outlined"
                color="warning"
                startIcon={revertingStock ? <CircularProgress size={16} /> : <RevertStockIcon />}
                onClick={handleRevertStock}
                disabled={revertingStock}
              >
                {revertingStock ? 'Reverting...' : 'Revert Stock'}
              </Button>
            ) : null}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', md: 'row' }} 
          gap={3}
          mb={3}
        >
          <Box flex={1}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Shipment Information</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Shipment Number</Typography>
                    <Typography variant="body1" fontWeight="medium">{shipment.shipment_number}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">From Location</Typography>
                    <Typography variant="body1">{shipment.from_location}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={shipment.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(shipment.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Expected Date</Typography>
                    <Typography variant="body1">
                      {shipment.expected_date ? format(new Date(shipment.expected_date), 'MMM dd, yyyy') : 'Not specified'}
                    </Typography>
                  </Box>
                  {shipment.received_date && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Received Date</Typography>
                      <Typography variant="body1">
                        {format(new Date(shipment.received_date), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box flex={1}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Additional Information</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created By</Typography>
                    <Typography variant="body1">
                      {`${shipment.created_by_name} ${shipment.created_by_lastname}`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                    <Typography variant="body1">
                      {format(new Date(shipment.created_at), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                  {shipment.received_by_name && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Received By</Typography>
                      <Typography variant="body1">
                        {`${shipment.received_by_name} ${shipment.received_by_lastname}`}
                      </Typography>
                    </Box>
                  )}
                  {shipment.status === 'received' && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Stock Status</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={shipment.stock_added ? (shipment.stock_reverted ? 'Reverted' : 'Added') : 'Not Added'}
                          color={shipment.stock_added ? (shipment.stock_reverted ? 'warning' : 'success') : 'default'}
                          size="small"
                        />
                      </Box>
                    </Box>
                  )}
                  {shipment.stock_added_by_name && shipment.stock_added_date && !shipment.stock_reverted && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Stock Added By</Typography>
                      <Typography variant="body1">{`${shipment.stock_added_by_name} ${shipment.stock_added_by_lastname}`}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(shipment.stock_added_date), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  )}
                  {shipment.stock_reverted_by_name && shipment.stock_reverted_date && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Stock Reverted By</Typography>
                      <Typography variant="body1">{`${shipment.stock_reverted_by_name} ${shipment.stock_reverted_by_lastname}`}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(shipment.stock_reverted_date), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Expected</Typography>
                    <Typography variant="body1">{getTotalExpectedQuantity()} units</Typography>
                  </Box>
                  {shipment.status === 'received' && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Received</Typography>
                      <Typography variant="body1">{getTotalReceivedQuantity()} units</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {shipment.notes && (
          <Box mb={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <Typography variant="body1">{shipment.notes}</Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Paper>

      {/* Items */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Shipment Items</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="center">Expected Box</TableCell>
                <TableCell align="center">Expected Single</TableCell>
                {shipment.status === 'received' && (
                  <>
                    <TableCell align="center">Received Box</TableCell>
                    <TableCell align="center">Received Single</TableCell>
                  </>
                )}
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shipment.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.category_name}</TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={getImageUrl(item.image_url)}
                      alt={item.product_name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.product_name}</Typography>
                    {Boolean(item.is_new_product) && (
                      <Chip label="New Product" size="small" color="info" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.unit_size && item.qty_in_box 
                      ? `${item.unit_size} x ${item.qty_in_box}`
                      : item.unit_size || '-'
                    }
                  </TableCell>
                  <TableCell align="center">{item.expected_box_quantity}</TableCell>
                  <TableCell align="center">{item.expected_single_quantity}</TableCell>
                  {shipment.status === 'received' && (
                    <>
                      <TableCell align="center">{item.received_box_quantity}</TableCell>
                      <TableCell align="center">{item.received_single_quantity}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.notes || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Receive Shipment {shipment.shipment_number}</Typography>
              <Typography variant="body2" color="text.secondary">
                This will mark the shipment as received. Use "Add to Stock" button later to update inventory.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              }
              label="Select All Expected"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Expected Box</TableCell>
                  <TableCell align="center">Expected Single</TableCell>
                  <TableCell align="center">Received Box</TableCell>
                  <TableCell align="center">Received Single</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipment.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          component="img"
                          src={getImageUrl(item.image_url)}
                          alt={item.product_name}
                          sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1 }}
                        />
                        <Box>
                          <Typography variant="body2">{item.sku}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.product_name}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{item.expected_box_quantity}</TableCell>
                    <TableCell align="center">{item.expected_single_quantity}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleReceiveQuantityChange(item.id, 'box', (receivedItems[item.id]?.box || 0) - 1)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={receivedItems[item.id]?.box || 0}
                          onChange={(e) => handleReceiveQuantityChange(item.id, 'box', Math.max(0, parseInt(e.target.value) || 0))}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ 
                            min: 0,
                            style: { textAlign: 'center', width: '50px' }
                          }}
                          sx={{ mx: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleReceiveQuantityChange(item.id, 'box', (receivedItems[item.id]?.box || 0) + 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleReceiveQuantityChange(item.id, 'single', (receivedItems[item.id]?.single || 0) - 1)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={receivedItems[item.id]?.single || 0}
                          onChange={(e) => handleReceiveQuantityChange(item.id, 'single', Math.max(0, parseInt(e.target.value) || 0))}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ 
                            min: 0,
                            style: { textAlign: 'center', width: '50px' }
                          }}
                          sx={{ mx: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleReceiveQuantityChange(item.id, 'single', (receivedItems[item.id]?.single || 0) + 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="Notes..."
                        value={receivedItems[item.id]?.notes || ''}
                        onChange={(e) => handleReceiveNotesChange(item.id, e.target.value)}
                        sx={{ minWidth: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReceiveShipment}
            variant="contained"
            disabled={receiving}
            startIcon={receiving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {receiving ? 'Receiving...' : 'Receive Shipment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Shipment Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current Status: <Chip 
                label={shipment?.status?.replace('_', ' ').toUpperCase()} 
                color={getStatusColor(shipment?.status || '') as any}
                size="small"
              />
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {getAvailableStatuses().map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {shipment?.stock_added && newStatus !== 'received' ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This shipment has stock added. You must revert stock before changing status.
              </Alert>
            ) : null}
            {shipment?.status === 'received' && newStatus !== 'received' ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Changing from "received" status will reset received quantities and dates.
              </Alert>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleStatusChange}
            variant="contained"
            disabled={updatingStatus || !newStatus || newStatus === shipment?.status || (shipment?.stock_added && newStatus !== 'received')}
            startIcon={updatingStatus ? <CircularProgress size={16} /> : <StatusIcon />}
          >
            {updatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentDetail;